import { z } from "zod";
import { reportingDateRangeQueryShape, validateReportingDateRange } from "./reporting";

/**
 * Campaign change history — the 操作日志 Tab. Rows mirror Amazon Ads change
 * history (Sponsored Products) with the operator resolved by AdPilot:
 * `user` / `ai` when the change matches one of AdPilot's own writes,
 * `external` (Amazon console or another tool) otherwise.
 */

/** 操作对象 — the kind of object a change applies to. */
export const changeObjectTypeSchema = z.enum([
  "campaign",
  "ad_group",
  "product_ad",
  "keyword_target",
  "product_target",
  "negative_target",
]);
export type ChangeObjectType = z.infer<typeof changeObjectTypeSchema>;

/**
 * 操作类型 — `{subject}.{action}`. Subjects refine the object type where the
 * reference UI does (category targets, negative keyword / product / brand).
 * `*.changed` covers a field Amazon reports that has no dedicated label.
 */
export const changeOperationTypeSchema = z.enum([
  "campaign.added",
  "campaign.archived",
  "campaign.enabled",
  "campaign.paused",
  "campaign.out_of_budget",
  "campaign.name_changed",
  "campaign.start_date_changed",
  "campaign.end_date_changed",
  "campaign.budget_increased",
  "campaign.budget_decreased",
  "campaign.portfolio_changed",
  "campaign.bidding_strategy_changed",
  "campaign.placement_changed",
  "campaign.changed",
  "ad_group.added",
  "ad_group.archived",
  "ad_group.enabled",
  "ad_group.paused",
  "ad_group.name_changed",
  "ad_group.bid_increased",
  "ad_group.bid_decreased",
  "ad_group.changed",
  "product_ad.added",
  "product_ad.archived",
  "product_ad.enabled",
  "product_ad.paused",
  "product_ad.changed",
  "keyword_target.added",
  "keyword_target.archived",
  "keyword_target.enabled",
  "keyword_target.paused",
  "keyword_target.bid_increased",
  "keyword_target.bid_decreased",
  "keyword_target.changed",
  "product_target.added",
  "product_target.archived",
  "product_target.enabled",
  "product_target.paused",
  "product_target.bid_increased",
  "product_target.bid_decreased",
  "product_target.changed",
  "category_target.added",
  "category_target.archived",
  "category_target.enabled",
  "category_target.paused",
  "category_target.bid_increased",
  "category_target.bid_decreased",
  "category_target.changed",
  "negative_keyword.added",
  "negative_keyword.archived",
  "negative_keyword.enabled",
  "negative_keyword.paused",
  "negative_keyword.changed",
  "negative_product.added",
  "negative_product.archived",
  "negative_product.enabled",
  "negative_product.paused",
  "negative_product.changed",
  "negative_brand.added",
  "negative_brand.archived",
  "negative_brand.enabled",
  "negative_brand.paused",
  "negative_brand.changed",
]);
export type ChangeOperationType = z.infer<typeof changeOperationTypeSchema>;

export const changeOperatorKindSchema = z.enum(["user", "ai", "external"]);
export type ChangeOperatorKind = z.infer<typeof changeOperatorKindSchema>;

/** Filter key of one operator: `ai`, `external`, or `user:<userId>`. */
export const changeOperatorKeySchema = z
  .string()
  .regex(/^(ai|external|user:[0-9a-f-]{36})$/i, "Invalid operator");

export const changeOperatorSchema = z.object({
  key: changeOperatorKeySchema,
  kind: changeOperatorKindSchema,
  /** The user's email for `user`; null for AI / external, or a deleted user. */
  email: z.string().nullable(),
});
export type ChangeOperator = z.infer<typeof changeOperatorSchema>;

export const campaignChangeEventSchema = z.object({
  id: z.string().uuid(),
  occurredAt: z.string().datetime({ offset: true }),
  objectType: changeObjectTypeSchema,
  operationType: changeOperationTypeSchema,
  /** Local object name, or the best name Amazon's event carries (keyword text, ASIN, id). */
  objectName: z.string(),
  /** 即时状态 — the object's current local status; null when it is not synced. */
  objectStatus: z.enum(["enabled", "paused", "archived"]).nullable(),
  adGroupName: z.string().nullable(),
  /** 字段 — Amazon's change type as reported (BID_AMOUNT, STATUS, CREATED, …). */
  field: z.string(),
  previousValue: z.string().nullable(),
  newValue: z.string().nullable(),
  operator: changeOperatorSchema,
});
export type CampaignChangeEvent = z.infer<typeof campaignChangeEventSchema>;

const csv = <T extends z.ZodTypeAny>(item: T) =>
  z
    .string()
    .optional()
    .transform((value) => (value ? value.split(",").filter(Boolean) : []))
    .pipe(z.array(item));

export const CHANGE_HISTORY_PAGE_SIZES = [10, 25, 50, 100, 200] as const;

export const campaignChangeHistoryQuerySchema = z
  .object({
    ...reportingDateRangeQueryShape,
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce
      .number()
      .int()
      .refine(
        (n) => (CHANGE_HISTORY_PAGE_SIZES as readonly number[]).includes(n),
        "Invalid page size",
      )
      .default(200),
    objectTypes: csv(changeObjectTypeSchema),
    operationTypes: csv(changeOperationTypeSchema),
    operators: csv(changeOperatorKeySchema),
    search: z.string().trim().max(200).optional(),
    /**
     * IANA zone the caller picked its dates in (the page's reporting time zone);
     * days are cut and timestamps meant to be read in it. Defaults to the
     * marketplace's zone.
     */
    timeZone: z
      .string()
      .max(64)
      .refine((zone) => {
        try {
          new Intl.DateTimeFormat("en-US", { timeZone: zone });
          return true;
        } catch {
          return false;
        }
      }, "Invalid time zone")
      .optional(),
  })
  .superRefine(validateReportingDateRange);
export type CampaignChangeHistoryQuery = z.infer<typeof campaignChangeHistoryQuerySchema>;

export const changeDailyCountSchema = z.object({
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  count: z.number().int().positive(),
});
export type ChangeDailyCount = z.infer<typeof changeDailyCountSchema>;

export const campaignChangeHistorySchema = z.object({
  /** Zone the window was cut in (the requested one, else the marketplace's); null → UTC. */
  timeZone: z.string().nullable(),
  currency: z.string().nullable(),
  shop: z.object({ name: z.string(), marketplace: z.string().nullable() }),
  /** Upper bound of this campaign's last mirror of Amazon's history; null before the first. */
  syncedThrough: z.string().datetime({ offset: true }).nullable(),
  /** Newest change of this campaign regardless of window and filters; null if none. */
  latestOccurredAt: z.string().datetime({ offset: true }).nullable(),
  /** Operators present in the window (before the operator filter), for the 操作人 options. */
  operators: z.array(changeOperatorSchema),
  /** Changes per local day in the window (before filters), ascending; days without changes omitted. */
  dailyCounts: z.array(changeDailyCountSchema),
  items: z.array(campaignChangeEventSchema),
});
export type CampaignChangeHistory = z.infer<typeof campaignChangeHistorySchema>;

export const campaignChangeHistoryResponseSchema = z.object({
  success: z.literal(true),
  data: campaignChangeHistorySchema,
  pagination: z.object({
    page: z.number().int(),
    pageSize: z.number().int(),
    total: z.number().int(),
    hasMore: z.boolean(),
  }),
});
export type CampaignChangeHistoryResponse = z.infer<typeof campaignChangeHistoryResponseSchema>;
